/**
 * /arena/:codeOrSlug — Public gym page + member link CTA.
 * Used when a user scans the gym's listing QR or shares the URL.
 */
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, MapPin, Clock, Star, Users } from "lucide-react";
import { apiClient } from "../../services/api";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";
import { ArenaShield, ArenaConfetti } from "../../components/arena/ArenaAtoms";

export default function ArenaGymPublic() {
  const { code } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [gym, setGym] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    apiClient.get(`/arena/gyms/${code}`)
      .then((r) => setGym(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "Gym not found"));
  }, [code]);

  const link = async () => {
    if (!user) {
      sessionStorage.setItem("post_login_redirect", `/arena/${code}`);
      navigate("/login");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post("/arena/link", { gym_code: gym.gym_code, method: "qr" });
      setSuccess({ gym_name: gym.gym_name, already: data.already_linked });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not link");
    } finally { setBusy(false); }
  };

  if (error) return (
    <div className="min-h-screen grid place-items-center px-6" style={{ background: "var(--bg-base, #ECEAE6)" }}>
      <div style={{ textAlign: "center", maxWidth: 420 }}>
        <h2 style={{ fontFamily: "Barlow Condensed", fontSize: 24, fontWeight: 800 }}>Gym not found</h2>
        <p style={{ fontSize: 14, color: "var(--text-secondary, #4A4845)", marginTop: 6 }}>{error}</p>
        <Link to="/arena/find" style={{ display: "inline-block", marginTop: 14, color: "#0C0C0B", textDecoration: "underline", fontWeight: 700 }}>
          Find another gym
        </Link>
      </div>
    </div>
  );

  if (!gym) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>Loading…</div>;

  if (success) return <ArenaLinkSuccess gymName={success.gym_name} onContinue={() => navigate("/home", { replace: true })} />;

  const isLinkedHere = gym.my_link?.gym_id === gym.gym_id;
  const photo = gym.photos?.[0];
  return (
    <div className="min-h-screen" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-gym-public">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}>
            <ChevronLeft size={22} />
          </button>
          <ArenaShield />
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pb-32">
        {photo && (
          <div style={{ marginTop: 16, aspectRatio: "16/9", overflow: "hidden", borderRadius: 16 }}>
            <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </div>
        )}
        <h1 style={{ fontFamily: "Barlow Condensed", fontSize: 30, fontWeight: 900, marginTop: 16 }}>{gym.gym_name} <span style={{ color: "#F5C518" }}>Arena</span></h1>
        <div style={{ display: "flex", gap: 12, marginTop: 8, fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><MapPin size={12} /> {gym.city}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Users size={12} /> {gym.fitfrik_members_count || 0} FitFrik members</span>
          {gym.rating > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Star size={12} /> {gym.rating.toFixed(1)}</span>}
        </div>
        <div style={{ marginTop: 14, padding: 14, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
          <div style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>{gym.address}</div>
        </div>
        {gym.amenities?.length > 0 && (
          <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", gap: 6 }}>
            {gym.amenities.map((a) => (
              <span key={a} style={{ padding: "5px 10px", background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 100, fontSize: 11, fontWeight: 600 }}>{a}</span>
            ))}
          </div>
        )}

        <div style={{ marginTop: 22, padding: 18, background: "linear-gradient(135deg, #F5C518 0%, #E4A30C 100%)", borderRadius: 16, color: "#0C0C0B" }}>
          {isLinkedHere ? (
            <>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 900 }}>You're linked here ✓</div>
              <p style={{ fontSize: 13, marginTop: 4 }}>Open the QR scanner from Home to check in.</p>
            </>
          ) : (
            <>
              <div style={{ fontFamily: "Barlow Condensed", fontSize: 22, fontWeight: 900 }}>Link your account</div>
              <p style={{ fontSize: 13, marginTop: 4 }}>
                Trainer notes, Arena Board, and your full fitness journey.
              </p>
              <button onClick={link} disabled={busy} data-testid="link-gym-btn"
                style={{ width: "100%", height: 52, marginTop: 14, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700 }}>
                {busy ? "Linking…" : `Link to ${gym.gym_name}`}
              </button>
              <button onClick={() => navigate(-1)} data-testid="not-my-gym"
                style={{ width: "100%", height: 44, marginTop: 8, background: "transparent", color: "#0C0C0B", border: "none", fontFamily: "Barlow", fontSize: 13, fontWeight: 600, textDecoration: "underline" }}>
                Not my gym
              </button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

function ArenaLinkSuccess({ gymName, onContinue }) {
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center px-6" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="arena-link-success">
      <ArenaConfetti />
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.4, ease: "easeOut" }}
        style={{ maxWidth: 380, textAlign: "center", position: "relative" }}
      >
        <motion.div
          initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
          style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}
        >
          <div style={{ position: "relative" }}>
            <div style={{ position: "absolute", inset: -14, background: "rgba(245,197,24,0.35)", borderRadius: "50%", filter: "blur(20px)" }} />
            <img src="/assets/arena/fitfrik-shield.jpg" alt="" width={72} height={72} style={{ position: "relative", borderRadius: "50%", objectFit: "cover" }} />
          </div>
        </motion.div>
        <h1 style={{ fontFamily: "Barlow Condensed", fontSize: 28, fontWeight: 900, color: "var(--text-primary, #0C0C0B)" }}>You are now an Arena Member</h1>
        <div style={{ marginTop: 4, fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, color: "#A37B00" }}>{gymName} Arena</div>
        <ul style={{ textAlign: "left", margin: "20px auto 0", maxWidth: 320, padding: 0, listStyle: "none", fontFamily: "Barlow", fontSize: 14, color: "var(--text-secondary, #4A4845)" }}>
          {[
            "Check in when you arrive.",
            "Trainer notes appear on arrival.",
            "Compete on the Arena Board.",
            "Your journey starts now.",
          ].map((t, i) => (
            <li key={i} style={{ display: "flex", gap: 10, padding: "5px 0" }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#F5C518", marginTop: 8, flexShrink: 0 }} />
              <span>{t}</span>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", marginTop: 14 }}>+30 FitPoints awarded</p>
        <button onClick={onContinue} data-testid="link-success-continue"
          style={{ width: "100%", height: 52, marginTop: 20, background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, fontFamily: "Barlow", fontSize: 14, fontWeight: 700 }}>
          Go to Home
        </button>
      </motion.div>
    </div>
  );
}
